import React, { useCallback, useEffect, useRef, useState } from "react";
import { UIText } from "@pfs/react-ui-kit";
import { Currency } from "src/utils/types";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { setPreviousTotalAmount } from "src/redux/slices/appSlice/AppSlice";
import { useTranslation } from "src/hooks/useTranslation";
import { TEXT_TAGS } from "src/utils/enums";

export type RoundingMode = "default" | "floor" | "ceil";

/** Matches common font-weight values; forwarded to UIText `weight`. */
export type FontWeight = number;

export interface UICurrencyProps {
  value: number | null;
  currency?: string;
  decimals?: number;
  roundingMode?: RoundingMode;
  /** Merged onto the outer wrapper (web). */
  className?: string;
  wrapperStyle?: React.CSSProperties;
  /** UIText `size` (px). */
  size?: number;
  color?: string;
  weight?: FontWeight;
  _animatedNumbers?: boolean;
  isAccessible?: boolean;
  startFromPreviousValue?: boolean;
  /** When false, hides subtree from assistive tech (web). */
  ariaHidden?: boolean;
  /** Exposed as `aria-label` on the wrapper when `isAccessible`. */
  accessibilityLabel?: string;
  preserveColor?: boolean;
  /** BCP 47 locale; defaults from current i18n language. */
  locale?: string;
  tag?: (typeof TEXT_TAGS)[keyof typeof TEXT_TAGS];
}

function validateCurrency(curr: string | null | undefined): string {
  if (!curr || typeof curr !== "string" || curr.trim().length === 0) {
    return Currency.ILS;
  }
  const trimmedCurrency = curr.trim().toUpperCase();
  if (trimmedCurrency.length !== 3 || !/^[A-Z]{3}$/.test(trimmedCurrency)) {
    return Currency.ILS;
  }
  return trimmedCurrency;
}

function applyRounding(val: number, roundingMode: RoundingMode, decimals: number): number {
  if (roundingMode === "default") {
    return val;
  }
  const multiplier = 10 ** decimals;
  if (roundingMode === "floor") {
    return Math.floor(val * multiplier) / multiplier;
  }
  if (roundingMode === "ceil") {
    return Math.ceil(val * multiplier) / multiplier;
  }
  return val;
}

/** Hebrew locales use CLDR suffix placement for some currencies; LTR locales keep the symbol on the left. */
function isHebrewBcp47Locale(loc: string): boolean {
  const normalized = loc.trim().toLowerCase().replace(/_/g, "-");
  return normalized === "he" || normalized.startsWith("he-");
}

function getCurrencyFormatLocale(validCurrency: string, locale: string): string {
  if (!isHebrewBcp47Locale(locale)) {
    return locale;
  }
  if (validCurrency === Currency.ILS) {
    return "en-IL";
  }
  if (validCurrency === Currency.USD) {
    return "en-US";
  }
  return locale;
}

const UICurrencyComponent: React.FC<UICurrencyProps> = ({
  value = 0,
  currency = Currency.ILS,
  decimals = 2,
  roundingMode = "default",
  className,
  wrapperStyle,
  size = 16,
  color,
  weight = 400,
  _animatedNumbers = false,
  isAccessible = true,
  startFromPreviousValue = false,
  ariaHidden = false,
  accessibilityLabel,
  preserveColor = false,
  locale: localeProp,
  tag = TEXT_TAGS.SPAN,
}) => {
  void _animatedNumbers;
  const { currentLanguage } = useTranslation();
  const locale =
    localeProp ?? (currentLanguage === "he" ? "he-IL" : "en-US");

  const { previousTotalAmount } = useAppSelector((state) => state.app);
  const dispatch = useAppDispatch();

  const safeValue =
    value === null || value === undefined || Number.isNaN(Number(value))
      ? 0
      : Number(value);

  const [displayValue, setDisplayValue] = useState(safeValue);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAnimatingRef = useRef(false);

  const runAnimation = useCallback(
    (fromValue: number, toValue: number) => {
      isAnimatingRef.current = true;
      setDisplayValue(fromValue);

      const steps = 30;
      const stepDuration = 50;
      const stepValue = (toValue - fromValue) / steps;

      const animateStep = (currentStep: number) => {
        if (currentStep >= steps) {
          setDisplayValue(toValue);
          dispatch(setPreviousTotalAmount(toValue));
          isAnimatingRef.current = false;
          return;
        }

        const newValue = fromValue + stepValue * currentStep;
        const safeNewValue = Number.isNaN(newValue) ? 0 : newValue;
        setDisplayValue(safeNewValue);

        timeoutRef.current = setTimeout(() => {
          animateStep(currentStep + 1);
        }, stepDuration);
      };

      animateStep(1);
    },
    [dispatch]
  );

  useEffect(() => {
    if (isAnimatingRef.current) {
      return undefined;
    }

    if (
      startFromPreviousValue &&
      previousTotalAmount >= 0 &&
      value != null &&
      value !== undefined &&
      value !== previousTotalAmount
    ) {
      runAnimation(previousTotalAmount, value);
    } else {
      setDisplayValue(value ?? 0);
      if (startFromPreviousValue && previousTotalAmount !== value) {
        dispatch(setPreviousTotalAmount(value ?? 0));
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, previousTotalAmount, startFromPreviousValue, dispatch, runAnimation]);

  const validCurrency = validateCurrency(currency);
  const roundedDisplayValue = applyRounding(displayValue, roundingMode, decimals);

  const formatLocale = getCurrencyFormatLocale(validCurrency, locale);

  const formatter = new Intl.NumberFormat(formatLocale, {
    style: "currency",
    currency: validCurrency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const formattedAmount = formatter.format(roundedDisplayValue);
  const a11yLabel = accessibilityLabel ?? formattedAmount;

  const mergedWrapperStyle: React.CSSProperties = {
    ...wrapperStyle,
    ...(preserveColor && color ? { color } : {}),
  };

  return (
    <span
      className={["ui-currency", className].filter(Boolean).join(" ")}
      style={Object.keys(mergedWrapperStyle).length ? mergedWrapperStyle : undefined}
      aria-label={isAccessible && !ariaHidden ? a11yLabel : undefined}
      aria-hidden={ariaHidden || !isAccessible ? true : undefined}
    >
      <UIText
        text={formattedAmount}
        tag={tag}
        size={size}
        weight={weight}
        ariaLabel={isAccessible ? a11yLabel : undefined}
        isAriaHidden={!isAccessible}
      />
    </span>
  );
};

function areEqual(prevProps: UICurrencyProps, nextProps: UICurrencyProps): boolean {
  return (
    prevProps.value === nextProps.value &&
    prevProps._animatedNumbers === nextProps._animatedNumbers &&
    prevProps.startFromPreviousValue === nextProps.startFromPreviousValue &&
    prevProps.color === nextProps.color &&
    prevProps.size === nextProps.size &&
    prevProps.weight === nextProps.weight &&
    prevProps.currency === nextProps.currency &&
    prevProps.decimals === nextProps.decimals &&
    prevProps.roundingMode === nextProps.roundingMode &&
    prevProps.preserveColor === nextProps.preserveColor &&
    prevProps.ariaHidden === nextProps.ariaHidden &&
    prevProps.accessibilityLabel === nextProps.accessibilityLabel &&
    prevProps.className === nextProps.className &&
    prevProps.locale === nextProps.locale &&
    prevProps.tag === nextProps.tag &&
    prevProps.isAccessible === nextProps.isAccessible &&
    prevProps.wrapperStyle === nextProps.wrapperStyle
  );
}

UICurrencyComponent.displayName = "UICurrency";

export const UICurrency = React.memo(UICurrencyComponent, areEqual);
